import React, { useState, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
  CircularProgress,
  Chip,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { debounce } from 'lodash';
import { PhoneInput } from './PhoneInput';
import { NationalIdInput, NationalIdType } from './NationalIdInput';
import apiClient from '../lib/axios';

interface PatientCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (patientId: string) => void;
}

interface InsuranceCompany {
  id: string;
  name: string;
}

interface RxNormDrug {
  rxcui: string;
  name: string;
  type: string;
}

interface PendingAllergy {
  rxcui?: string;
  name: string;
}

const patientSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  phone: z.string().optional(),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  nationalId: z.string().min(1, 'National ID is required'),
  nationalIdType: z.enum(['CEDULA', 'PASSPORT']),
  passportCountry: z.string().optional(),
  dateOfBirth: z.string().optional(),
  chronicConditions: z.string().optional(),
  insuranceCompanyId: z.string().optional(),
  insuranceNumber: z.string().optional(),
  generalNotes: z.string().optional(),
  shareWithOtherDoctors: z.boolean(),
});

type PatientFormData = z.infer<typeof patientSchema>;

const STEPS = ['patients.step1Title', 'patients.step2Title'];

export const PatientCreateDialog: React.FC<PatientCreateDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(0);
  const [pendingAllergies, setPendingAllergies] = useState<PendingAllergy[]>([]);
  const [allergySearchTerm, setAllergySearchTerm] = useState('');
  const [allergyInputValue, setAllergyInputValue] = useState('');

  // Debounced search term for RxNorm API calls
  const debouncedSetAllergySearch = useMemo(
    () => debounce((value: string) => setAllergySearchTerm(value), 300),
    []
  );

  // Search RxNorm API for medications
  const { data: rxNormResults = [], isLoading: isSearchingRxNorm } = useQuery({
    queryKey: ['rxnorm-search', allergySearchTerm],
    queryFn: async () => {
      if (!allergySearchTerm || allergySearchTerm.length < 2) return [];
      const { data } = await apiClient.get<RxNormDrug[]>('/patients/rxnorm/search', {
        params: { name: allergySearchTerm },
      });
      return data;
    },
    enabled: allergySearchTerm.length >= 2,
  });

  const { data: insuranceCompanies = [], isLoading: isLoadingInsurance } = useQuery({
    queryKey: ['insurance-companies'],
    queryFn: async () => {
      const { data } = await apiClient.get<InsuranceCompany[]>('/insurance-companies');
      return data;
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PatientFormData>({
    resolver: zodResolver(patientSchema),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      nationalId: '',
      nationalIdType: 'CEDULA',
      passportCountry: '',
      dateOfBirth: '',
      chronicConditions: '',
      insuranceCompanyId: '',
      insuranceNumber: '',
      generalNotes: '',
      shareWithOtherDoctors: true,
    },
  });

  const nationalIdType = watch('nationalIdType');
  const nationalId = watch('nationalId');
  const passportCountry = watch('passportCountry');

  const createMutation = useMutation({
    mutationFn: async (data: PatientFormData) => {
      // Clean empty strings from optional fields
      const cleanedData = {
        ...data,
        phone: data.phone || undefined,
        email: data.email || undefined,
        passportCountry: data.passportCountry || undefined,
        dateOfBirth: data.dateOfBirth || undefined,
        chronicConditions: data.chronicConditions || undefined,
        insuranceCompanyId: data.insuranceCompanyId || undefined,
        insuranceNumber: data.insuranceNumber || undefined,
        generalNotes: data.generalNotes || undefined,
      };

      // First, create the patient
      const response = await apiClient.post('/patients', cleanedData);
      const patientId = response.data.id;

      // Then, add each pending allergy
      for (const allergy of pendingAllergies) {
        await apiClient.post(`/patients/${patientId}/allergies`, {
          rxcui: allergy.rxcui,
          name: allergy.name,
        });
      }

      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      reset();
      setActiveStep(0);
      setPendingAllergies([]);
      setAllergyInputValue('');
      setAllergySearchTerm('');
      onClose();
      if (onSuccess) {
        onSuccess(data.id);
      }
    },
  });

  const onSubmit = (data: PatientFormData) => {
    createMutation.mutate(data);
  };

  const handleClose = () => {
    reset();
    setActiveStep(0);
    setPendingAllergies([]);
    setAllergyInputValue('');
    setAllergySearchTerm('');
    onClose();
  };

  const handleAllergyInputChange = (_event: React.SyntheticEvent, value: string) => {
    setAllergyInputValue(value);
    debouncedSetAllergySearch(value);
  };

  const handleSelectAllergy = (_event: React.SyntheticEvent, value: RxNormDrug | string | null) => {
    if (!value) return;

    let newAllergy: PendingAllergy;
    if (typeof value === 'string') {
      // Free text entry
      newAllergy = { name: value };
    } else {
      // Selected from RxNorm results
      newAllergy = { rxcui: value.rxcui, name: value.name };
    }

    // Check if already added (by name, case-insensitive)
    const alreadyExists = pendingAllergies.some(
      (a) => a.name.toLowerCase() === newAllergy.name.toLowerCase()
    );

    if (!alreadyExists) {
      setPendingAllergies([...pendingAllergies, newAllergy]);
    }

    setAllergyInputValue('');
    setAllergySearchTerm('');
  };

  const handleRemoveAllergy = (index: number) => {
    setPendingAllergies(pendingAllergies.filter((_, i) => i !== index));
  };

  const handleNext = async () => {
    // Validate step 1 fields before proceeding
    const isValid = await trigger(['name', 'nationalId', 'dateOfBirth', 'phone', 'email']);
    if (isValid) {
      setActiveStep(1);
    }
  };

  const handleBack = () => {
    setActiveStep(0);
  };

  const renderStep1 = () => (
    <Grid container spacing={2}>
      {/* Name */}
      <Grid item xs={12}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={t('patients.fullName')}
              required
              error={!!errors.name}
              helperText={errors.name?.message}
            />
          )}
        />
      </Grid>

      {/* National ID with Type Selector */}
      <Grid item xs={12}>
        <NationalIdInput
          nationalIdType={nationalIdType as NationalIdType}
          nationalId={nationalId || ''}
          passportCountry={passportCountry}
          onNationalIdTypeChange={(type) => setValue('nationalIdType', type)}
          onNationalIdChange={(value) => setValue('nationalId', value)}
          onPassportCountryChange={(country) => setValue('passportCountry', country)}
          error={!!errors.nationalId}
          helperText={errors.nationalId?.message}
        />
      </Grid>

      {/* Date of Birth */}
      <Grid item xs={12} sm={6}>
        <Controller
          name="dateOfBirth"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={t('patients.dateOfBirth')}
              type="date"
              InputLabelProps={{ shrink: true }}
              error={!!errors.dateOfBirth}
              helperText={errors.dateOfBirth?.message}
            />
          )}
        />
      </Grid>

      {/* Phone */}
      <Grid item xs={12} sm={6}>
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <PhoneInput
              value={field.value || ''}
              onChange={field.onChange}
              error={!!errors.phone}
              helperText={errors.phone?.message}
            />
          )}
        />
      </Grid>

      {/* Email */}
      <Grid item xs={12}>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={t('patients.email')}
              type="email"
              error={!!errors.email}
              helperText={errors.email?.message}
            />
          )}
        />
      </Grid>
    </Grid>
  );

  const renderStep2 = () => (
    <Grid container spacing={2}>
      {/* Allergies */}
      <Grid item xs={12}>
        <Box>
          {/* Pending Allergy Chips */}
          {pendingAllergies.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {pendingAllergies.map((allergy, index) => (
                <Chip
                  key={index}
                  label={allergy.name}
                  onDelete={() => handleRemoveAllergy(index)}
                  deleteIcon={
                    <IconButton size="small" sx={{ p: 0.25 }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  }
                  variant="outlined"
                  sx={{
                    '& .MuiChip-deleteIcon': {
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'error.main',
                      },
                    },
                  }}
                />
              ))}
            </Box>
          )}

          {/* Allergy Search Input */}
          <Autocomplete
            freeSolo
            options={rxNormResults}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
            inputValue={allergyInputValue}
            onInputChange={handleAllergyInputChange}
            onChange={handleSelectAllergy}
            loading={isSearchingRxNorm}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('patients.allergies')}
                placeholder={t('patients.searchMedication')}
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isSearchingRxNorm ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <li key={key} {...otherProps}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      RxCUI: {option.rxcui} | {option.type}
                    </Typography>
                  </Box>
                </li>
              );
            }}
            ListboxProps={{
              style: { maxHeight: 250 },
            }}
            noOptionsText={
              allergySearchTerm.length < 2
                ? t('patients.typeToSearch')
                : t('patients.noMedicationsFound')
            }
          />
        </Box>
      </Grid>

      {/* Chronic Conditions */}
      <Grid item xs={12}>
        <Controller
          name="chronicConditions"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={t('patients.chronicConditions')}
              multiline
              rows={2}
              placeholder={t('patients.chronicConditionsPlaceholder')}
              error={!!errors.chronicConditions}
              helperText={errors.chronicConditions?.message}
            />
          )}
        />
      </Grid>

      {/* Insurance Company */}
      <Grid item xs={12} sm={6}>
        <Controller
          name="insuranceCompanyId"
          control={control}
          render={({ field }) => (
            <Autocomplete
              options={insuranceCompanies}
              getOptionLabel={(option) => option.name}
              value={insuranceCompanies.find((c) => c.id === field.value) || null}
              onChange={(_, newValue) => {
                field.onChange(newValue?.id || '');
              }}
              loading={isLoadingInsurance}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('patients.insuranceCompany')}
                  placeholder={t('patients.selectInsuranceCompany')}
                  error={!!errors.insuranceCompanyId}
                  helperText={errors.insuranceCompanyId?.message}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingInsurance ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
        />
      </Grid>

      {/* Insurance Number */}
      <Grid item xs={12} sm={6}>
        <Controller
          name="insuranceNumber"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={t('patients.insuranceNumber')}
              placeholder={t('patients.insuranceNumberPlaceholder')}
              error={!!errors.insuranceNumber}
              helperText={errors.insuranceNumber?.message}
            />
          )}
        />
      </Grid>

      {/* General Notes */}
      <Grid item xs={12}>
        <Controller
          name="generalNotes"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={t('patients.generalNotes')}
              multiline
              rows={3}
              placeholder={t('patients.generalNotesPlaceholder')}
              error={!!errors.generalNotes}
              helperText={errors.generalNotes?.message}
            />
          )}
        />
      </Grid>

      {/* Share With Other Doctors */}
      <Grid item xs={12}>
        <Controller
          name="shareWithOtherDoctors"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={field.value} />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {t('patients.shareWithDoctors')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('patients.shareWithDoctorsDesc')}
                  </Typography>
                </Box>
              }
            />
          )}
        />
      </Grid>
    </Grid>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('patients.createPatient')}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {createMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('patients.failedToCreate')}
            </Alert>
          )}

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {STEPS.map((labelKey) => (
              <Step key={labelKey}>
                <StepLabel>{t(labelKey)}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          {activeStep === 0 ? renderStep1() : renderStep2()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={createMutation.isPending}>
            {t('common.cancel')}
          </Button>
          {activeStep === 0 ? (
            <Button variant="contained" onClick={handleNext}>
              {t('patients.next')}
            </Button>
          ) : (
            <>
              <Button onClick={handleBack} disabled={createMutation.isPending}>
                {t('patients.back')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? t('patients.creating') : t('patients.create')}
              </Button>
            </>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};
